import {
  forwardRef,
  useId,
  type ButtonHTMLAttributes,
  type MouseEvent,
  type ReactNode,
} from "react";
import { cx } from "./cx";
import "./Switch.css";

export type SwitchValue = boolean | "mixed";

export interface SwitchProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "onChange" | "value"> {
  /** `"mixed"` = bulk selection whose rows differ. Activating a mixed switch turns it on. */
  checked: SwitchValue;
  onChange?: (next: boolean) => void;
  label?: ReactNode;
  hint?: ReactNode;
  size?: "sm" | "md";
  /** Put the label before the track (settings rows) instead of after it. */
  labelPosition?: "start" | "end";
}

/**
 * On/off toggle. Uses `role="switch"`; in the mixed state it reports
 * `role="checkbox" aria-checked="mixed"` because ARIA switches cannot be mixed.
 * Without a visible `label`, pass `aria-label`.
 */
export const Switch = forwardRef<HTMLButtonElement, SwitchProps>(function Switch(
  {
    checked,
    onChange,
    label,
    hint,
    size = "md",
    labelPosition = "end",
    disabled,
    id,
    className,
    onClick,
    ...rest
  },
  ref,
) {
  const auto = useId();
  const controlId = id ?? `sw${auto}`;
  const hintId = hint ? `${controlId}-hint` : undefined;
  const mixed = checked === "mixed";
  const state = mixed ? "mixed" : checked ? "on" : "off";

  const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
    onClick?.(e);
    if (e.defaultPrevented || disabled) return;
    onChange?.(mixed ? true : !checked);
  };

  const control = (
    <button
      ref={ref}
      id={controlId}
      type="button"
      role={mixed ? "checkbox" : "switch"}
      aria-checked={mixed ? "mixed" : checked}
      aria-describedby={hintId}
      disabled={disabled}
      data-state={state}
      className={cx("ui-switch__control", `ui-switch--${size}`)}
      onClick={handleClick}
      {...rest}
    >
      <span className="ui-switch__track" aria-hidden="true">
        <span className="ui-switch__thumb" />
      </span>
    </button>
  );

  return (
    <span
      className={cx(
        "ui-switch",
        labelPosition === "start" && "ui-switch--label-start",
        disabled && "ui-switch--disabled",
        className,
      )}
    >
      {control}
      {label || hint ? (
        <span className="ui-switch__text">
          {label ? (
            <label className="ui-switch__label" htmlFor={controlId}>
              {label}
            </label>
          ) : null}
          {hint ? (
            <span className="ui-switch__hint" id={hintId}>
              {hint}
            </span>
          ) : null}
        </span>
      ) : null}
    </span>
  );
});
